/**
 * LinesSection 訂單明細 (S3.2) — PO lines with negotiated prices, the
 * linked SO refs (first-class Allocations, rendered on BOTH documents) and
 * the 檢查第二次 second-check aids snapshot.
 */

import { Table, Tag, Tooltip, theme } from 'antd';
import type { TableProps } from 'antd';
import { AuditOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TableContainer, { renderDash } from '@/components/common/TableContainer';
import PurpleTag from '@/components/common/PurpleTag';
import { SectionHeader } from '@/components/products/ProductDetailPage/sections/SectionHeader';
import type { PurchaseOrder, PurchaseOrderLine } from '@/types/api/purchaseOrder';
import { formatAmount, formatMoney } from '../../../salesOrders/components/soFormat';

interface LinesSectionProps {
  po: PurchaseOrder;
}

export const LinesSection = ({ po }: LinesSectionProps) => {
  const { t } = useTranslation(['procurement', 'common']);
  const { token } = theme.useToken();
  const navigate = useNavigate();

  const columns: TableProps<PurchaseOrderLine>['columns'] = [
    {
      title: t('procurement:fields.product'),
      key: 'product',
      width: 260,
      render: (_: unknown, line) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>{line.sku}</span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {line.productName}
          </span>
        </div>
      ),
    },
    {
      title: t('procurement:fields.qty'),
      dataIndex: 'qty',
      key: 'qty',
      width: 90,
      align: 'right',
      render: (qty: number) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{qty}</span>
      ),
    },
    {
      title: t('procurement:fields.purchasePrice'),
      key: 'purchasePrice',
      width: 190,
      align: 'right',
      render: (_: unknown, line) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5 tw-items-end">
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(line.currency, line.purchasePrice)}
          </span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {line.priceSource === 'vendor_line'
              ? t('procurement:detail.lines.priceSourceVendorLine')
              : t('procurement:detail.lines.priceSourceManual')}
          </span>
        </div>
      ),
    },
    {
      title: t('procurement:fields.expectedDate'),
      dataIndex: 'expectedDate',
      key: 'expectedDate',
      width: 130,
      render: (value: string | null) => value || renderDash(),
    },
    {
      title: t('procurement:fields.lineTotal'),
      key: 'lineTotal',
      width: 150,
      align: 'right',
      render: (_: unknown, line) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatAmount(line.qty * line.purchasePrice)}
        </span>
      ),
    },
    {
      title: t('procurement:detail.lines.allocations'),
      key: 'allocations',
      width: 240,
      render: (_: unknown, line) => {
        const allocations = line.allocations ?? [];
        if (allocations.length === 0) {
          return (
            <span style={{ fontSize: token.fontSizeSM, color: token.colorTextTertiary }}>
              {t('procurement:detail.lines.noAllocations')}
            </span>
          );
        }
        return (
          <div className="tw-flex tw-flex-wrap tw-gap-1 tw-items-center">
            {allocations.map((alloc) => (
              <Tag
                key={alloc.id}
                style={{ margin: 0, cursor: 'pointer' }}
                onClick={() => navigate(`/auth/sales-orders/${alloc.soId}`)}
              >
                {alloc.soNumber} ×{alloc.qty}
              </Tag>
            ))}
            {allocations.some((alloc) => alloc.mode === 'aggregate') && (
              <PurpleTag style={{ margin: 0 }}>
                {t('procurement:detail.lines.aggregate')}
              </PurpleTag>
            )}
          </div>
        );
      },
    },
    {
      title: t('procurement:detail.lines.secondCheck'),
      key: 'secondCheck',
      width: 130,
      render: (_: unknown, line) => {
        if (!line.secondCheck) return renderDash();
        const { openPoCount, lowestRecentPrice, duplicateOpenQty, warnings } = line.secondCheck;
        const notes = [
          t('procurement:detail.lines.secondCheckOpenPos', { count: openPoCount }),
          lowestRecentPrice != null
            ? t('procurement:detail.lines.secondCheckLowest', {
                price: formatAmount(lowestRecentPrice),
              })
            : null,
          duplicateOpenQty > 0
            ? t('procurement:detail.lines.secondCheckDuplicate', { qty: duplicateOpenQty })
            : null,
        ].filter(Boolean);
        return (
          <Tooltip
            title={
              <div className="tw-flex tw-flex-col tw-gap-1">
                {notes.length > 0
                  ? notes.map((note) => <span key={String(note)}>{note}</span>)
                  : t('procurement:detail.lines.secondCheckClear')}
              </div>
            }
          >
            <span
              className="tw-inline-flex tw-items-center tw-gap-1"
              style={{
                color: warnings.length > 0 ? token.colorWarning : token.colorTextSecondary,
                cursor: 'help',
              }}
            >
              <AuditOutlined />
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{warnings.length}</span>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div>
      <SectionHeader
        title={t('procurement:detail.lines.title')}
        subtitle={t('procurement:detail.lines.subtitle')}
      />
      <TableContainer>
        <Table<PurchaseOrderLine>
          rowKey="id"
          size="small"
          dataSource={po.lines}
          columns={columns}
          pagination={false}
          scroll={{ x: 1090 }}
        />
      </TableContainer>
    </div>
  );
};

export default LinesSection;
